import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Search, FileText, FolderKanban, Ticket, Rocket, Mountain, Puzzle, User, X, Sparkles, ArrowRight,
} from "lucide-react";

const TYPE_ICON = {
  document: FileText, task: FileText, cr: Ticket,
  project: FolderKanban, epic: Mountain, feature: Puzzle,
  release: Rocket, employee: User,
};
const TYPE_LABEL = { document: "Doc", task: "Task", cr: "CR", project: "Project", epic: "Epic", feature: "Feature", release: "Release", employee: "Person" };
const TYPE_ORDER = ["document", "cr", "task", "project", "epic", "feature", "employee"];

/**
 * "Ask PRIZM" command palette.
 * Opens on ⌘K / Ctrl+K, searches /api/search live (debounced 200ms),
 * groups hits by type with keyboard navigation, Enter opens the match.
 */
export default function AskPrizmPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [grouped, setGrouped] = useState({});
  const [flat, setFlat] = useState([]);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // ⌘K / Ctrl+K to open; Esc to close
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [open]);
  useEffect(() => { if (!open) { setQ(""); setGrouped({}); setFlat([]); setIdx(0); } }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setGrouped({}); setFlat([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        const g = data.grouped || {};
        setGrouped(g);
        const ordered = TYPE_ORDER.flatMap((t) => (g[t] || []).slice(0, 6));
        setFlat(ordered); setIdx(0);
      } catch { setGrouped({}); setFlat([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const navTo = (hit) => { setOpen(false); nav(hit.href); };

  // Navigate between results with arrow keys
  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && flat[idx]) { e.preventDefault(); navTo(flat[idx]); }
  };

  if (!open) return null;

  // Portal to body so the overlay escapes TopNav's backdrop-filter stacking
  // context (otherwise `fixed inset-0` only covers the main column, not the
  // sidebar).
  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-24"
      onClick={() => setOpen(false)}
      data-testid="ask-prizm-overlay"
    >
      <div
        className="w-full max-w-2xl glass-strong border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="ask-prizm"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <Sparkles className="w-4 h-4 text-primary" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Ask PRIZM — search docs, CRs, tasks, projects, people…"
            className="flex-1 border-0 bg-transparent focus-visible:ring-0 h-9 text-sm"
            data-testid="ask-prizm-input"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded bg-background hairline text-muted-foreground">esc</kbd>
          <button onClick={() => setOpen(false)} className="h-7 w-7 flex items-center justify-center text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="max-h-[65vh] overflow-y-auto">
          {!q.trim() ? (
            <div className="px-4 py-10 text-center">
              <div className="text-sm font-medium">Start typing to search across PRIZM</div>
              <div className="text-xs text-muted-foreground mt-1">Documentation · CRs · Tasks · Projects · Epics · Features · People</div>
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                <span>Try:</span>
                {["payment gateway", "sprint retro", "onboarding"].map((s) => (
                  <button key={s} onClick={() => setQ(s)} className="rounded-full border px-2 py-0.5 hover:border-primary/40">{s}</button>
                ))}
              </div>
            </div>
          ) : loading && flat.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Searching…</div>
          ) : flat.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">No matches for "{q}".</div>
          ) : (
            TYPE_ORDER.map((t) => {
              const rows = (grouped[t] || []).slice(0, 6);
              if (!rows.length) return null;
              return (
                <div key={t} className="py-1" data-testid={`ask-prizm-group-${t}`}>
                  <div className="px-4 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">{TYPE_LABEL[t]}</div>
                  {rows.map((h) => {
                    const Icon = TYPE_ICON[t] || Search;
                    const isActive = flat[idx]?.id === h.id && flat[idx]?.type === h.type;
                    return (
                      <button
                        key={`${t}-${h.id}`}
                        onClick={() => navTo(h)}
                        onMouseEnter={() => setIdx(flat.findIndex((x) => x.id === h.id && x.type === h.type))}
                        className={`w-full flex items-center gap-3 px-4 py-2 text-left ${isActive ? "bg-primary/15" : "hover:bg-accent/40"}`}
                        data-testid={`ask-prizm-hit-${h.id}`}
                      >
                        <Icon className={`w-4 h-4 ${isActive ? "text-primary" : "text-muted-foreground"} shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{h.title}</div>
                          {h.subtitle && <div className="text-[10px] text-muted-foreground truncate">{h.subtitle}</div>}
                        </div>
                        {isActive && <ArrowRight className="w-3.5 h-3.5 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <div className="px-4 py-2 border-t text-[10px] text-muted-foreground flex items-center gap-3">
          <span><kbd className="px-1 py-0.5 rounded bg-background border text-[9px]">↑↓</kbd> navigate</span>
          <span><kbd className="px-1 py-0.5 rounded bg-background border text-[9px]">↵</kbd> open</span>
          <span><kbd className="px-1 py-0.5 rounded bg-background border text-[9px]">esc</kbd> close</span>
          <span className="ml-auto">Powered by PRIZM</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
